import React, { createContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { User } from '../types';
import { authService } from '../services/authApi';
import { useInactivityTimer } from '../hooks/useInactivityTimer';
import { SessionWarningModal } from '../components/ui/SessionWarningModal';
import { showSessionNotification } from '../utils/sessionNotifications';
import {
  useLogin,
  useLogout,
  useMe,
  useNavigation,
  useCheckSessionExpiry,
  useRefreshToken,
} from '../hooks/useAuthApi';
import { NavItem } from '../constants/sidebar';
// Navigation adapter removed - backend now provides frontend-compatible structure directly
import { logger } from '../utils/logger';
import { resolveActiveEstablishment } from '../utils/activeEstablishment';
import { clearCache } from '../hooks/useApiQuery';
import apiClient from '../services/api/client';

// Define the shape of our context
interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  permissions: string[];
  navigation: NavItem[];
  isLoading: boolean;
  isLoggingOut: boolean;
  refreshSession: () => Promise<void>;
  establishments: Array<{ id: string; name: string; roles: string[] }>;
  establishmentsLoading: boolean;
}

// Create the context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  login: async () => {},
  logout: () => {},
  isAuthenticated: false,
  permissions: [],
  navigation: [],
  isLoading: true,
  isLoggingOut: false,
  refreshSession: async () => {},
  establishments: [],
  establishmentsLoading: true,
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [navigation, setNavigation] = useState<NavItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [warningTimeLeft, setWarningTimeLeft] = useState(0);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const suppressWarningRef = React.useRef<number>(0);
  const navigate = useNavigate();
  
  const useRealAuth = true;
  
  // React Query hooks
  const loginMutation = useLogin();
  const logoutMutation = useLogout();
  const refreshTokenMutation = useRefreshToken();
  const hasToken = !!localStorage.getItem('mep_token');
  const hasEstablishment = !!localStorage.getItem('mep_current_establishment');
  const { data: meData, isLoading: isMeLoading, error: meError, refetch: refetchMe } = useMe(useRealAuth && hasToken);
  const { data: navData, isLoading: isNavLoading } = useNavigation(useRealAuth && hasToken && hasEstablishment);
  const { data: sessionData } = useCheckSessionExpiry(useRealAuth && !!user);


  // Função para limpar o estado de autenticação
  const clearAuthState = useCallback(() => {
    setUser(null);
    setPermissions([]);
    setNavigation([]);
    localStorage.removeItem('mep_token');
    localStorage.removeItem('mep_user');
    localStorage.removeItem('mep_current_establishment');
    if (apiClient.defaults?.headers?.common) {
      delete apiClient.defaults.headers.common.Authorization;
      delete apiClient.defaults.headers.common['x-establishment-id'];
    }
    if (typeof window !== 'undefined') {
      const maybeApiClient = (window as { apiClient?: typeof apiClient }).apiClient;
      if (maybeApiClient?.defaults?.headers?.common) {
        delete maybeApiClient.defaults.headers.common.Authorization;
        delete maybeApiClient.defaults.headers.common['x-establishment-id'];
      }
    }
    setShowSessionWarning(false);
    // Remove identidade no Sentry para que erros pós-logout não sejam
    // atribuídos ao usuário anterior.
    try {
      Sentry.setUser(null);
    } catch {
      // ignora falha de instrumentação
    }
    // Limpa todo o cache de queries sem disparar refetch para evitar dados residuais do usuário anterior
    // clearCache() already dispatches 'auth-logout' event for EstablishmentContext to clear its state
    clearCache();
  }, []);

  // Função de logout
  const logout = useCallback(async () => {
    const token = localStorage.getItem('mep_token');
    setIsLoggingOut(true);
    
    if (useRealAuth) {
      try {
        await logoutMutation.mutateAsync();
      } catch (error) {
        logger.error('Erro no logout', error instanceof Error ? error : new Error(String(error)), {
          module: 'auth-context',
          action: 'logout'
        });
      }
    } else {
      if (token) {
        try {
          await authService.logout(token);
        } catch (error) {
          logger.error('Erro no logout', error instanceof Error ? error : new Error(String(error)), {
          module: 'auth-context',
          action: 'logout'
        });
        }
      }
    }
    clearAuthState();
    setIsLoggingOut(false);
    navigate('/');
  }, [useRealAuth, logoutMutation, clearAuthState, navigate]);

  // Handler para quando a sessão expira por inatividade
  const handleInactivityTimeout = useCallback(() => {
    setShowSessionWarning(false);
    logout();
  }, [logout]);

  // Handler para mostrar aviso de expiração
  const handleSessionWarning = useCallback((remainingTime: number) => {
    // Evita reabrir imediatamente após estender a sessão
    const now = Date.now();
    if (suppressWarningRef.current && now - suppressWarningRef.current < 3000) {
      return;
    }
    setWarningTimeLeft(Math.floor(remainingTime / 1000));
    setShowSessionWarning(true);
  }, []);

  // Timer de inatividade
  const { resetTimer } = useInactivityTimer({
    timeout: 15 * 60 * 1000, // 15 minutos
    onTimeout: handleInactivityTimeout,
    onWarning: handleSessionWarning,
    warningTime: 2 * 60 * 1000, // Avisa 2 minutos antes
    enabled: !!user
  });

  // Função para estender a sessão
  const extendSession = useCallback(async () => {
    setShowSessionWarning(false);
    setWarningTimeLeft(Math.floor((2 * 60 * 1000) / 1000));

    try {
      suppressWarningRef.current = Date.now();
      await refreshTokenMutation.mutateAsync();
      resetTimer();
      showSessionNotification({ type: 'extended' });
      
      logger.info('Sessão estendida com sucesso', {
        module: 'auth-context',
        action: 'extend-session'
      });
    } catch (error) {
      logger.error('Erro ao estender sessão', error instanceof Error ? error : new Error(String(error)), {
        module: 'auth-context',
        action: 'extend-session'
      });
      showSessionNotification({ type: 'expired' });
      logout();
    }
  }, [refreshTokenMutation, resetTimer, logout]);

  // Função para verificar token armazenado na inicialização
  useEffect(() => {
    const checkStoredToken = async () => {
      const storedToken = localStorage.getItem('mep_token');

      if (useRealAuth) {
        // For real auth, data comes from React Query hooks
        if (storedToken && meData) {
          // Successfully loaded user data
          const establishment = resolveActiveEstablishment(
            meData.establishments,
            localStorage.getItem('mep_current_establishment'),
          );

          setUser({
            id: meData.id,
            name: meData.name,
            email: meData.email,
            roles: establishment?.roles ?? [],
            establishmentId: establishment?.id,
            establishment: establishment ? {
              id: establishment.id,
              name: establishment.name,
              document: '', // Not provided in UserResponse
              createdAt: new Date(),
              updatedAt: new Date(),
              createdBy: undefined,
              updatedBy: undefined,
              deletedAt: null,
              deletedBy: undefined
            } : undefined,
            establishments: meData.establishments,
            phone: null,
            unit: null,
            active: true, // Not provided in UserResponse, default to true
            createdAt: new Date(),
            updatedAt: new Date()
          });
          setPermissions(meData.permissions || []);
          // Identifica o usuário no Sentry para todos os erros subsequentes.
          try {
            Sentry.setUser({
              id: meData.id,
              email: meData.email,
              username: meData.name,
            });
          } catch {
            // ignora falha de instrumentação
          }
        } else if (storedToken && meError && !isMeLoading) {
          // Token exists but /auth/me failed (token expired or invalid)
          // Clear auth state and let ProtectedRoute redirect to login
          logger.info('[AuthContext] Token expired or invalid, clearing auth state', {
            module: 'auth-context',
            action: 'token-expired'
          });
          localStorage.removeItem('mep_token');
          localStorage.removeItem('mep_user');
          localStorage.removeItem('mep_current_establishment');
          setUser(null);
          setPermissions([]);
          navigate('/');
        } else if (!storedToken) {
          // No token stored - user is not authenticated
          setUser(null);
          setPermissions([]);
        }
        // If storedToken exists but meData is undefined and no error, it means the query is still loading
        // Don't clear the user state in this case
        // console.log('[AuthContext] Navigation data received:', navData);
        if (navData && navData.navigation && Array.isArray(navData.navigation)) {
          // Backend provides navigation in an object with navigation array
          // console.log('[AuthContext] Setting navigation from navData.navigation:', navData.navigation);
          setNavigation(navData.navigation);
        } else if (navData && Array.isArray(navData)) {
          // Direct array format
          // console.log('[AuthContext] Setting navigation from direct array:', navData);
          setNavigation(navData);
        } else if (!storedToken) {
          // Only clear navigation if there's no token
          // console.log('[AuthContext] Clearing navigation - no token');
          setNavigation([]);
        } else {
          // console.log('[AuthContext] Navigation data format not recognized:', navData);
        }
        // If token exists but navigation data hasn't loaded yet, keep existing navigation
      } else {
        // Mock auth flow - use empty permissions for mock
        const storedUser = localStorage.getItem('mep_user');
        if (storedToken && storedUser) {
          try {
            const validatedUser = await authService.validateToken(storedToken);
            const parsedStoredUser = JSON.parse(storedUser) as User;
            
            // Verifica se o usuário validado coincide com o armazenado
            if (validatedUser.id === parsedStoredUser.id) {
              setUser(validatedUser);
              // Extract permissions from JWT token payload
              try {
                const payloadSegment = storedToken.split('.')[1];
                if (payloadSegment) {
                  const tokenPayload = JSON.parse(atob(payloadSegment)) as { permissions?: string[] };
                  setPermissions(tokenPayload.permissions ?? []);
                } else {
                  setPermissions([]);
                }
              } catch {
                setPermissions([]);
              }
            } else {
              // Remove dados inválidos
              localStorage.removeItem('mep_token');
              localStorage.removeItem('mep_user');
            }
          } catch (error) {
            logger.error('Token inválido', error instanceof Error ? error : new Error(String(error)), {
              module: 'auth-context',
              action: 'token-decode'
            });
            localStorage.removeItem('mep_token');
            localStorage.removeItem('mep_user');
          }
        }
      }
      setIsInitialized(true);
    };

    checkStoredToken();
  }, [useRealAuth, meData, meError, isMeLoading, navData, navigate]);
  
  // Update loading state based on real auth queries
  useEffect(() => {
    if (useRealAuth) {
      const token = !!localStorage.getItem('mep_token');
      const est = !!localStorage.getItem('mep_current_establishment');
      // Consider loading until we have both user and navigation when token exists
      setIsLoading(token && (!est ? isMeLoading : (isMeLoading || isNavLoading)));
    } else {
      setIsLoading(!isInitialized);
    }
  }, [useRealAuth, isMeLoading, isNavLoading, isInitialized]);

  // Função de login
  const login = useCallback(async (email: string, password: string) => {
    try {
      if (useRealAuth) {
        // Legacy real auth flow
        await loginMutation.mutateAsync({ email, password });
        // Evita aviso de sessão logo após login
        suppressWarningRef.current = Date.now();

        // Fetch user data after successful login
        // Note: useMe now sets establishment and axios header directly
        await refetchMe();

        // Wait until user and establishment are stored
        const waitUntil = async (predicate: () => boolean, timeoutMs = 2000, stepMs = 50) => {
          const start = Date.now();
          while (Date.now() - start < timeoutMs) {
            if (predicate()) return true;
            await new Promise(r => setTimeout(r, stepMs));
          }
          return false;
        };

        await waitUntil(() =>
          !!localStorage.getItem('mep_user') &&
          !!localStorage.getItem('mep_current_establishment')
        );

        navigate('/dashboard');
      } else {
        // Mock auth flow
        const response = await authService.login({ email, password });
        
        setUser(response.user);
        setPermissions([]);
        suppressWarningRef.current = Date.now();
        
        // Armazena token e usuário
        localStorage.setItem('mep_token', response.token);
        localStorage.setItem('mep_user', JSON.stringify(response.user));
        
        navigate('/dashboard');
      }
    } catch (error) {
      logger.error('Erro no login', error instanceof Error ? error : new Error(String(error)), {
        module: 'auth-context',
        action: 'login'
      });
      throw error;
    }
  }, [useRealAuth, loginMutation, refetchMe, navigate]);

  // Função para atualizar a sessão manualmente
  const refreshSession = useCallback(async () => {
    const token = localStorage.getItem('mep_token');
    
    if (useRealAuth) {
      try {
        // Para JWTs stateless, refetch user data e reset timer
        await refetchMe();
        resetTimer();
      } catch (error) {
        logger.error('Erro ao atualizar sessão', error instanceof Error ? error : new Error(String(error)), {
          module: 'auth-context',
          action: 'update-session'
        });
        logout();
      }
    } else {
      if (!token) return;
      
      try {
        // Mock auth - apenas reset timer
        resetTimer();
      } catch (error) {
        logger.error('Erro ao atualizar sessão', error instanceof Error ? error : new Error(String(error)), {
          module: 'auth-context',
          action: 'update-session'
        });
        logout();
      }
    }
  }, [useRealAuth, refetchMe, resetTimer, logout]);

  // Verifica periodicamente se a sessão está próxima de expirar
  useEffect(() => {
    if (!user) return;

    const token = localStorage.getItem('mep_token');

    if (useRealAuth) {
      // For real auth, session data comes from React Query hook
      if (sessionData?.willExpire && !showSessionWarning) {
        handleSessionWarning(2 * 60 * 1000); // 2 minutes warning
      }
      return undefined; // Add explicit return for this path
    } else {
      // Mock auth flow
      if (!token) return;
      
      const checkSessionExpiry = async () => {
        try {
          const { willExpire } = await authService.checkSessionExpiry(token);
          if (willExpire && !showSessionWarning) {
            // Sessão vai expirar, mas ainda não está mostrando o aviso
            // O timer de inatividade cuidará disso
          }
        } catch (error) {
          console.error('Erro ao verificar expiração da sessão:', error);
          logout();
        }
      };

      const interval = setInterval(checkSessionExpiry, 30000); // Verifica a cada 30 segundos
      return () => clearInterval(interval);
    }
  }, [useRealAuth, user, sessionData, showSessionWarning, handleSessionWarning, logout]);

  const contextValue: AuthContextType = {
    user,
    login,
    logout,
    isAuthenticated: !!user || !!localStorage.getItem('mep_token'),
    permissions,
    navigation,
    isLoading,
    isLoggingOut,
    refreshSession,
    establishments: meData?.establishments || [],
    establishmentsLoading: isMeLoading,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      <SessionWarningModal
        isOpen={showSessionWarning}
        remainingTime={warningTimeLeft}
        onExtendSession={extendSession}
        onLogout={logout}
      />
    </AuthContext.Provider>
  );
};

export default AuthContext;
